import React, { useEffect, useState } from "react";

import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { GoogleMapsMarker } from "@src/features/dashboard/components/google_maps_marker.tsx";
import { GoogleMapsMarkerProps } from "@src/features/dashboard/components/google_maps_marker.tsx/google_maps_marker";
import {
  getMapPosition,
  getMapsOptions,
} from "@src/features/dashboard/utils/request_country_statistics_utils";
import { RequestCountryStatistics } from "@src/types/dashboard_types";
import { Card } from "antd";
import "./request_country_statistics_chart.less";

export interface RequestCountryStatisticsChartProps {
  requestCountryStatisticsData: RequestCountryStatistics[];
  isLoading: boolean;
}

type GoogleMapsMarkers = Pick<
  GoogleMapsMarkerProps,
  "position" | "totalRequests"
>;

const RequestCountryStatisticsChart: React.FC<
  RequestCountryStatisticsChartProps
> = ({ requestCountryStatisticsData, isLoading }) => {
  const [googleMapsMarker, setGoogleMapsMarker] = useState<
    Map<string, GoogleMapsMarkers>
  >(new Map());

  const { isLoaded: isApiLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    if (isApiLoaded && !isLoading && requestCountryStatisticsData.length > 0) {
      calculateMarker();
    }
  }, [isApiLoaded, requestCountryStatisticsData, isLoading]);

  function calculateMarker() {
    for (const requestCountryStatisticsDataElements of requestCountryStatisticsData) {
      const geocoder: google.maps.Geocoder = new window.google.maps.Geocoder();

      geocoder
        .geocode({ address: requestCountryStatisticsDataElements.countryName })
        ?.then((results: google.maps.GeocoderResponse) => {
          const googleMapsMarker: GoogleMapsMarkers = {
            position: results.results[0].geometry.location,
            totalRequests: requestCountryStatisticsDataElements.totalRequests,
          };

          setGoogleMapsMarker((prevMap: Map<string, GoogleMapsMarkers>) =>
            new Map(prevMap).set(
              requestCountryStatisticsDataElements.countryName,
              googleMapsMarker
            )
          );
        })
        .catch((err) => console.log(err));
    }
  }

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }

  return (
    <Card
      title="Requests"
      className="request-country-statistics-chart"
      loading={isLoading || !isApiLoaded}
      data-testid="request-country-statistics-chart"
    >
      <GoogleMap
        mapContainerClassName="request-country-statistics-chart__google-maps-chart"
        center={getMapPosition()}
        zoom={1}
        options={getMapsOptions()}
        data-testid="google-maps"
        onLoad={calculateMarker}
      >
        {Array.from(googleMapsMarker).map(([key, value]) => (
          <GoogleMapsMarker
            key={key}
            countryName={key}
            totalRequests={value.totalRequests}
            position={value.position}
          />
        ))}
      </GoogleMap>
    </Card>
  );
};

export default RequestCountryStatisticsChart;
